// The built-in fonts a design may name. The list is fonts.json, held to the server's
// registry (internal/layout/fonts.go) by fonts_test.go; the faces are ../fonts/fonts.css.
import { useEffect, useState } from 'react'
import list from './fonts.json'
import type { ElementKind, LayoutCanvas, LayoutDocument, LayoutElement } from './types'

export interface DesignFont {
  id: string
  name: string
  category: 'text' | 'display'
  weights: number[]
}

export const FONTS = list as DesignFont[]

/** The face every design drew in before fonts: TT Fors on screens, DejaVu Sans on paper. */
export const STANDARD_FONT = 'standard'

/** Kinds with no words take no font (the server refuses one). */
const WORDLESS: ReadonlySet<ElementKind> = new Set<ElementKind>(['qr', 'image', 'rule', 'media', 'logo', 'panel'])

export function hasText(kind: ElementKind): boolean {
  return !WORDLESS.has(kind)
}

export function knownFont(id: string): boolean {
  return FONTS.some((f) => f.id === id)
}

/** The CSS family list for a font id; standard and unknown ids are the body face. */
export function fontStack(id: string | undefined): string | undefined {
  if (!id || id === STANDARD_FONT || !knownFont(id)) return undefined
  return `"qf-${id}", var(--font-body)`
}

/** An element's font: its own, else the design's; undefined is standard. */
export function fontOf(canvas: Pick<LayoutCanvas, 'font'>, el: Pick<LayoutElement, 'font'>): string | undefined {
  const id = el.font || canvas.font
  return id && id !== STANDARD_FONT && knownFont(id) ? id : undefined
}

/** Every chosen font a design draws with, each once. */
export function designFonts(doc: Pick<LayoutDocument, 'canvas' | 'elements'>): string[] {
  const out = new Set<string>()
  for (const el of doc.elements) {
    if (el.hidden || !hasText(el.kind)) continue
    const id = fontOf(doc.canvas, el)
    if (id) out.add(id)
  }
  return [...out]
}

/** The canvas with its font chosen; standard is stored as absent, as every older design has it. */
export function withCanvasFont(canvas: LayoutCanvas, id: string): LayoutCanvas {
  const { font: _, ...rest } = canvas
  return id === STANDARD_FONT || !id ? rest : { ...rest, font: id }
}

/** The element with its own font, or inheriting the design's when id is ''. */
export function withElementFont(el: LayoutElement, id: string): LayoutElement {
  const { font: _, ...rest } = el
  return id ? { ...rest, font: id } : rest
}

// One letter per unicode-range part of a face (Latin, Latin Extended, Cyrillic), so each is fetched.
const SAMPLE = 'Aə Ж'

/** Starts loading a design's fonts; resolves when they are in (or failed, or not offered here). */
export function loadDesignFonts(doc: Pick<LayoutDocument, 'canvas' | 'elements'>): Promise<void> {
  const set = typeof document !== 'undefined' ? document.fonts : undefined
  const ids = designFonts(doc)
  if (!set || ids.length === 0) return Promise.resolve()
  const weights = (id: string) => FONTS.find((f) => f.id === id)?.weights ?? [400]
  const loads = ids.flatMap((id) => weights(id).map((w) => set.load(`${w} 32px "qf-${id}"`, SAMPLE).catch(() => [])))
  return Promise.all(loads).then(() => undefined)
}

/** Fetches every weight and script of a design's fonts up front, so a later call never waits on one. */
export function usePreloadDesignFonts(doc: Pick<LayoutDocument, 'canvas' | 'elements'>): void {
  const key = designFonts(doc).join(',')
  useEffect(() => {
    if (key) void loadDesignFonts(doc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
}

/** Whether a font has loaded, for measuring text in it; true at once for the standard face. */
export function useFontLoaded(id: string | undefined): boolean {
  const set = typeof document !== 'undefined' ? document.fonts : undefined
  const probe = id ? `400 32px "qf-${id}"` : ''
  const [loaded, setLoaded] = useState<string>(() => (!probe || !set || set.check(probe, SAMPLE) ? probe : ''))
  useEffect(() => {
    if (!probe || !set || loaded === probe) return
    let live = true
    void set
      .load(probe, SAMPLE)
      .catch(() => [])
      .then(() => live && setLoaded(probe))
    return () => {
      live = false
    }
  }, [probe, set, loaded])
  return loaded === probe
}

/** The Font select's options, each drawn in its own face. */
export function fontOptions(standard: string, display: string): Array<{ value: string; label: string; note?: string; fontFamily: string }> {
  return FONTS.map((f) => ({
    value: f.id,
    label: f.id === STANDARD_FONT ? standard : f.name,
    note: f.category === 'display' ? display : undefined,
    fontFamily: fontStack(f.id) ?? 'var(--font-body)',
  }))
}
